import type { GameData, HitData, Player, SavedHighscore } from '@/types/game';
import { formatGameNumber, normalizeTeamName, hasTeamName } from './game-utils';
import { computePlayerStats, MIN_SHOTS_FOR_ACCURACY, MIN_TAKEN_FOR_KD } from './player-stats';

// Thresholds live in the shared stats module so PDF and records agree.
export { MIN_SHOTS_FOR_ACCURACY, MIN_TAKEN_FOR_KD };

export interface GameRef {
  gameNumber: string;
  date: string;
}

export interface RecordEntry {
  /** Stable key for React + tests. */
  key: string;
  label: string;
  /** Preformatted value string. */
  value: string;
  /** Who achieved it (player or team name). */
  holder: string;
  game: GameRef;
  /** Small clarifying note, e.g. the qualifying threshold. */
  note?: string;
  /** Raw numeric value, used for sorting/highlighting. */
  raw: number;
}

export interface TopScoreEntry {
  key: string;
  name: string;
  score: number;
  accuracy: number;
  game: GameRef;
  /** Spieltag (Europe/Berlin) als "YYYY-MM-DD". */
  dayKey: string;
}

export interface RecordsResult {
  gamesAnalyzed: number;
  gamesWithHitData: number;
  records: RecordEntry[];
  /** Nur die besten Einzelergebnisse des jüngsten Spieltags. */
  topScores: TopScoreEntry[];
  /** Spieltag der Liste, "DD.MM.YYYY" (leer, wenn keine Spiele). */
  dayLabel: string;
  /** True, wenn dieser Spieltag der heutige Kalendertag in Europe/Berlin ist. */
  dayIsToday: boolean;
}

/** "YYYY-MM-DD" in Europe/Berlin — der Tagesvergleich darf nicht in UTC kippen. */
const berlinDayKey = (date: Date): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

const dayKeyOf = (row: SavedHighscore): string => {
  const match = String(row.game_data?.timestamp ?? '').match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const d = new Date(row.game_started_at ?? '');
  return Number.isNaN(d.getTime()) ? '' : berlinDayKey(d);
};


const toInt = (value: unknown): number => {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/** "DD.MM.YYYY - HH:MM Uhr" -> "DD.MM.YYYY"; falls back to the ISO start time. */
const gameDate = (gameData: GameData | undefined, startedAt?: string): string => {
  const match = String(gameData?.timestamp ?? '').match(/(\d{2}\.\d{2}\.\d{4})/);
  if (match) return match[1];
  if (startedAt) {
    const d = new Date(startedAt);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('de-DE');
  }
  return '';
};

const gameRef = (row: SavedHighscore): GameRef => ({
  gameNumber: formatGameNumber(row.game_data?.game ?? row.game_id ?? ''),
  date: gameDate(row.game_data, row.game_started_at),
});

const validPlayers = (gameData: GameData | undefined): Player[] =>
  (Array.isArray(gameData?.players) ? gameData!.players : []).filter(
    (p) => p && String(p.name ?? '').trim() !== '',
  );

const de = (n: number, digits = 0) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/**
 * Everything is derived from the games already in the client cache — no fetching,
 * no schema knowledge beyond what SavedHighscore already carries.
 * Players are identified by name only; there is no player id in the source data.
 */
export const computeRecords = (rows: SavedHighscore[]): RecordsResult => {
  const allGames = (Array.isArray(rows) ? rows : []).filter((r) => r && r.game_data);

  // Bezugsrahmen = jüngster Spieltag (Europe/Berlin), bevor irgendetwas berechnet wird.
  let latestDayKey = '';
  const dayKeys = allGames.map((row) => {
    const key = dayKeyOf(row);
    if (key && key > latestDayKey) latestDayKey = key;
    return key;
  });
  const games = allGames.filter((_, i) => dayKeys[i] === latestDayKey && latestDayKey !== '');

  let gamesWithHitData = 0;

  type Best = { raw: number; holder: string; game: GameRef; extra?: number };
  let bestScore: Best | undefined;
  let bestAccuracy: Best | undefined;
  let mostShots: Best | undefined;
  let mostDealt: Best | undefined;
  let bestKd: Best | undefined;
  let bestTeamScore: Best | undefined;

  const keepMax = (current: Best | undefined, candidate: Best) =>
    !current || candidate.raw > current.raw ? candidate : current;

  const allScores: TopScoreEntry[] = [];

  games.forEach((row) => {
    let gd: GameData;
    let ref: GameRef;
    try {
      gd = row.game_data;
      ref = gameRef(row);
    } catch {
      return;
    }

    const players = validPlayers(gd);
    if (players.length === 0) return;

    const dayKey = dayKeyOf(row);

    const hits: HitData[] | null | undefined = Array.isArray(row.hit_data) ? row.hit_data : null;
    const hasHits = !!hits && hits.length > 0;
    if (hasHits) gamesWithHitData += 1;

    let stats: ReturnType<typeof computePlayerStats> | undefined;
    if (hasHits) {
      try {
        stats = computePlayerStats(players, hits, gd.teams);
      } catch {
        stats = undefined;
      }
    }

    players.forEach((player) => {
      const name = String(player.name).trim();
      const score = toInt(player.score);
      const shots = toInt(player.shots);
      const accuracy = toInt(player.accurancy);

      allScores.push({
        key: `${ref.gameNumber}-${player.weste}-${name}`,
        name,
        score,
        accuracy,
        game: ref,
        dayKey,
      });


      bestScore = keepMax(bestScore, { raw: score, holder: name, game: ref });
      mostShots = keepMax(mostShots, { raw: shots, holder: name, game: ref });

      if (shots >= MIN_SHOTS_FOR_ACCURACY && accuracy > 0) {
        bestAccuracy = keepMax(bestAccuracy, { raw: accuracy, holder: name, game: ref });
      }

      const s = stats?.get(`${player.weste}-${name}`);
      if (s?.hitsDealt !== undefined) {
        mostDealt = keepMax(mostDealt, { raw: s.hitsDealt, holder: name, game: ref });
      }
      if (s?.kd !== undefined && (s.hitsTaken ?? 0) >= MIN_TAKEN_FOR_KD) {
        bestKd = keepMax(bestKd, { raw: s.kd, holder: name, game: ref });
      }
    });

    (Array.isArray(gd.teams) ? gd.teams : []).forEach((team) => {
      if (!hasTeamName(team?.team)) return;
      bestTeamScore = keepMax(bestTeamScore, {
        raw: toInt(team.score),
        holder: `Team ${normalizeTeamName(team.team)}`,
        game: ref,
      });
    });
  });

  const records: RecordEntry[] = [];
  const push = (
    key: string,
    label: string,
    best: Best | undefined,
    format: (raw: number) => string,
    note?: string,
  ) => {
    if (!best || !Number.isFinite(best.raw)) return;
    records.push({
      key,
      label,
      value: format(best.raw),
      holder: best.holder,
      game: best.game,
      note,
      raw: best.raw,
    });
  };

  push('score', 'Höchste Punktzahl', bestScore, (v) => de(v));
  push('accuracy', 'Beste Trefferquote', bestAccuracy, (v) => `${de(v)} %`, `ab ${MIN_SHOTS_FOR_ACCURACY} Schüssen`);
  push('shots', 'Meiste Schüsse', mostShots, (v) => de(v));
  push('dealt', 'Meiste Treffer ausgeteilt', mostDealt, (v) => de(v));
  push('kd', 'Bestes K/D', bestKd, (v) => de(v, 1), `ab ${MIN_TAKEN_FOR_KD} kassierten Treffern`);
  push('teamscore', 'Höchster Teamscore', bestTeamScore, (v) => de(v));

  // Alle verbliebenen Spiele gehören bereits zum jüngsten Spieltag.
  const topScores = allScores
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  const [y, m, d] = latestDayKey.split('-');
  const dayLabel = latestDayKey ? `${d}.${m}.${y}` : '';

  return {
    gamesAnalyzed: games.length,
    gamesWithHitData,
    records,
    topScores,
    dayLabel,
    dayIsToday: latestDayKey !== '' && latestDayKey === berlinDayKey(new Date()),

  };
};
